import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MaterialsCRUD.css';

const MaterialsCRUD = () => {
  const [materials, setMaterials] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userRole, setUserRole] = useState(null);
  const [canManageAllCustomers, setCanManageAllCustomers] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    material_type: '',
    customer_id: '',
    search: '',
    access_level: ''
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    material_type: 'books',
    target_customer_id: '',
    subject_id: '',
    topic_id: '',
    access_level: 'public',
    tags: '',
    duration: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);

  // Get user role from token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role_id);
      } catch (err) {
        console.error('Error parsing token:', err);
      }
    }
  }, []);

  // Fetch initial data
  useEffect(() => {
    if (userRole) {
      fetchMaterials();
      fetchMetadata();
    }
  }, [userRole, currentPage, filters]);

  // Fetch materials for CRUD
  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...filters
      });

      // Remove empty filters
      Object.keys(filters).forEach(key => {
        if (!filters[key]) {
          params.delete(key);
        }
      });

      const response = await axios.get(`/api/materials/crud/list?${params}`);
      
      setMaterials(response.data.materials);
      setCurrentPage(response.data.pagination.current_page);
      setTotalPages(response.data.pagination.total_pages);
      setCanManageAllCustomers(response.data.can_manage_all_customers);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch materials');
      console.error('Error fetching materials:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch metadata (customers, subjects, initial topics)
  const fetchMetadata = async () => {
    try {
      const requests = [
        axios.get('/api/subjects')
      ];

      // Only Super Admin can fetch customers
      if (userRole === 4) {
        requests.push(axios.get('/api/materials/crud/customers'));
      }

      const responses = await Promise.all(requests);
      
      setSubjects(responses[0].data || []);
      // Don't load all topics initially - they'll be loaded based on subject selection
      setTopics([]);
      
      if (userRole === 4 && responses[1]) {
        setCustomers(responses[1].data.customers || []);
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  // Fetch topics based on selected subject
  const fetchTopicsBySubject = async (subjectId) => {
    if (!subjectId) {
      setTopics([]);
      return;
    }

    try {
      const response = await axios.get(`/api/subjects/${subjectId}/topics`);
      setTopics(response.data || []);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setTopics([]);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    setCurrentPage(1);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If subject is changed, reset topic and fetch new topics
    if (name === 'subject_id') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        topic_id: '' // Reset topic selection
      }));
      
      // Fetch topics for the selected subject
      fetchTopicsBySubject(value);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
    
    if (file) {
      // Auto-detect file format
      const extension = file.name.split('.').pop().toLowerCase();
      setFormData(prev => ({
        ...prev,
        file_format: extension
      }));
    }
  };

  // Open create modal
  const openCreateModal = () => {
    setFormData({
      title: '',
      description: '',
      material_type: 'books',
      target_customer_id: '',
      subject_id: '',
      topic_id: '',
      access_level: 'public',
      tags: '',
      duration: ''
    });
    setSelectedFile(null);
    setShowCreateModal(true);
  };

  // Open edit modal
  const openEditModal = (material) => {
    setSelectedMaterial(material);
    setFormData({
      title: material.title || '',
      description: material.description || '',
      material_type: material.material_type || 'books',
      target_customer_id: material.customer_id || '',
      subject_id: material.subject_id || '',
      topic_id: material.topic_id || '',
      access_level: material.access_level || 'public',
      tags: material.tags ? JSON.stringify(material.tags) : '',
      duration: material.duration || ''
    });
    setSelectedFile(null);
    
    // If material has a subject, fetch its topics
    if (material.subject_id) {
      fetchTopicsBySubject(material.subject_id);
    } else {
      setTopics([]);
    }
    
    setShowEditModal(true);
  };

  // Handle create material
  const handleCreateMaterial = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      // Prepare payload
      const payload = {
        ...formData,
        tags: formData.tags ? JSON.parse(formData.tags) : null,
        duration: formData.duration ? parseInt(formData.duration) : null,
        subject_id: formData.subject_id || null,
        topic_id: formData.topic_id || null,
        file_size: selectedFile?.size || null,
        file_format: formData.file_format || selectedFile?.name.split('.').pop()
      };

      // Create material
      const response = await axios.post('/api/materials', payload);
      
      // Upload file if provided using backend proxy (avoids CORS issues)
      if (selectedFile && response.data.material?.id) {
        try {
          console.log('📤 Uploading file via backend proxy...');
          console.log('📁 File:', selectedFile.name, 'Size:', selectedFile.size, 'Type:', selectedFile.type);
          
          // Create FormData for file upload
          const formData = new FormData();
          formData.append('file', selectedFile);
          
          // Upload file through backend proxy
          const uploadResponse = await axios.post(`/api/materials/${response.data.material.id}/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          console.log('✅ File uploaded successfully via proxy!');
          console.log('📊 Upload response:', uploadResponse.data);
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          console.error('Upload error details:', uploadError.response?.data || uploadError.message);
          alert('Material created but file upload failed. Please try again.');
        }
      }

      setShowCreateModal(false);
      fetchMaterials();
      alert(response.data.message || 'Material created successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create material');
    } finally {
      setUploading(false);
    }
  };

  // Handle update material
  const handleUpdateMaterial = async (e) => {
    e.preventDefault();
    setUploading(true);
    setError('');

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        material_type: formData.material_type,
        subject_id: formData.subject_id || null,
        topic_id: formData.topic_id || null,
        access_level: formData.access_level,
        tags: formData.tags ? JSON.parse(formData.tags) : null,
        duration: formData.duration ? parseInt(formData.duration) : null
      };

      await axios.put(`/api/materials/${selectedMaterial.id}`, payload);
      
      setShowEditModal(false);
      fetchMaterials();
      alert('Material updated successfully!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update material');
    } finally {
      setUploading(false);
    }
  };

  // Handle delete material
  const handleDeleteMaterial = async (materialId) => {
    if (!window.confirm('Are you sure you want to delete this material?')) {
      return;
    }

    try {
      await axios.delete(`/api/materials/${materialId}`);
      fetchMaterials();
      alert('Material deleted successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete material');
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Get material type icon
  const getMaterialTypeIcon = (type) => {
    switch (type) {
      case 'books':
        return '📚';
      case 'handwritten_notes':
        return '📝';
      case 'lectures':
        return '🎥';
      default:
        return '📄';
    }
  };

  // Get access level badge
  const getAccessLevelBadge = (level) => {
    const badges = {
      public: { class: 'badge-success', text: 'Public' },
      premium: { class: 'badge-warning', text: 'Premium' },
      restricted: { class: 'badge-danger', text: 'Restricted' }
    };
    
    const badge = badges[level] || badges.public;
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  return (
    <div className="materials-crud">
      <div className="crud-header">
        <div className="header-content">
          <h1>📚 Materials Management</h1>
          <p>Create, edit, and manage educational materials</p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={openCreateModal}
        >
          ➕ Add Material
        </button>
      </div>

      {/* Filters */}
      <div className="crud-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search materials..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Type:</label>
            <select
              value={filters.material_type}
              onChange={(e) => handleFilterChange('material_type', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="books">📚 Books</option>
              <option value="handwritten_notes">📝 Handwritten Notes</option>
              <option value="lectures">🎥 Lectures</option>
            </select>
          </div>

          {canManageAllCustomers && (
            <div className="filter-group">
              <label>Customer:</label>
              <select
                value={filters.customer_id}
                onChange={(e) => handleFilterChange('customer_id', e.target.value)}
              >
                <option value="">All Customers</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <label>Access Level:</label>
            <select
              value={filters.access_level}
              onChange={(e) => handleFilterChange('access_level', e.target.value)}
            >
              <option value="">All Levels</option>
              <option value="public">Public</option>
              <option value="premium">Premium</option>
              <option value="restricted">Restricted</option>
            </select>
          </div>
        </div>
      </div>

      {/* Materials Table */}
      {loading ? (
        <div className="loading">Loading materials...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : (
        <>
          <div className="materials-table">
            <table>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Type</th>
                  <th>Customer</th>
                  <th>Subject</th>
                  <th>Access Level</th>
                  <th>Views</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {materials.map(material => (
                  <tr key={material.id}>
                    <td>
                      <div className="material-info">
                        <h4>{material.title}</h4>
                        {material.description && (
                          <p className="material-description">
                            {material.description.length > 100 
                              ? `${material.description.substring(0, 100)}...` 
                              : material.description}
                          </p>
                        )}
                        {material.file_size && (
                          <small>📁 {formatFileSize(material.file_size)}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="material-type">
                        {getMaterialTypeIcon(material.material_type)} {material.material_type.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      {material.customer?.name || 'N/A'}
                    </td>
                    <td>
                      {material.subject?.name || 'N/A'}
                    </td>
                    <td>
                      {getAccessLevelBadge(material.access_level)}
                    </td>
                    <td>
                      👁️ {material.view_count}
                    </td>
                    <td>
                      <small>
                        {new Date(material.created_at).toLocaleDateString()}
                        <br />
                        by {material.creator?.name}
                      </small>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-sm btn-secondary"
                          onClick={() => openEditModal(material)}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteMaterial(material.id)}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="btn btn-secondary"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                ← Previous
              </button>
              
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              
              <button 
                className="btn btn-secondary"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Create Material Modal */}
      {showCreateModal && (
        <MaterialModal
          title="Add New Material"
          formData={formData}
          customers={customers}
          subjects={subjects}
          topics={topics}
          canManageAllCustomers={canManageAllCustomers}
          selectedFile={selectedFile}
          uploading={uploading}
          error={error}
          onInputChange={handleInputChange}
          onFileSelect={handleFileSelect}
          onSubmit={handleCreateMaterial}
          onClose={() => setShowCreateModal(false)}
          isEdit={false}
        />
      )}

      {/* Edit Material Modal */}
      {showEditModal && (
        <MaterialModal
          title="Edit Material"
          formData={formData}
          customers={customers}
          subjects={subjects}
          topics={topics}
          canManageAllCustomers={canManageAllCustomers}
          selectedFile={selectedFile}
          uploading={uploading}
          error={error}
          onInputChange={handleInputChange}
          onFileSelect={handleFileSelect}
          onSubmit={handleUpdateMaterial}
          onClose={() => setShowEditModal(false)}
          isEdit={true}
        />
      )}
    </div>
  );
};

// Material Modal Component
const MaterialModal = ({ 
  title, 
  formData, 
  customers, 
  subjects, 
  topics, 
  canManageAllCustomers,
  selectedFile,
  uploading,
  error,
  onInputChange, 
  onFileSelect, 
  onSubmit, 
  onClose,
  isEdit 
}) => {
  return (
    <div className="modal-overlay">
      <div className="modal modal-large">
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={onSubmit} className="modal-form">
          {error && <div className="error">{error}</div>}

          <div className="form-row">
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                required
                value={formData.title}
                onChange={onInputChange}
              />
            </div>

            <div className="form-group">
              <label>Material Type *</label>
              <select
                name="material_type"
                required
                value={formData.material_type}
                onChange={onInputChange}
              >
                <option value="books">📚 Books</option>
                <option value="handwritten_notes">📝 Handwritten Notes</option>
                <option value="lectures">🎥 Lectures</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={onInputChange}
              rows="3"
            />
          </div>

          {canManageAllCustomers && (
            <div className="form-row">
              <div className="form-group">
                <label>Target Customer *</label>
                <select
                  name="target_customer_id"
                  required
                  value={formData.target_customer_id}
                  onChange={onInputChange}
                >
                  <option value="">Select Customer</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name} ({customer.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Subject</label>
              <select
                name="subject_id"
                value={formData.subject_id}
                onChange={onInputChange}
              >
                <option value="">Select Subject</option>
                {subjects.map(subject => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Topic</label>
              <select
                name="topic_id"
                value={formData.topic_id}
                onChange={onInputChange}
                disabled={!formData.subject_id}
              >
                <option value="">
                  {!formData.subject_id 
                    ? "Select Subject First" 
                    : topics.length === 0 
                      ? "No Topics Available" 
                      : "Select Topic"
                  }
                </option>
                {topics.map(topic => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name}
                  </option>
                ))}
              </select>
              {!formData.subject_id && (
                <small className="form-help-text">
                  Please select a subject first to see available topics
                </small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Access Level</label>
              <select
                name="access_level"
                value={formData.access_level}
                onChange={onInputChange}
              >
                <option value="public">Public</option>
                <option value="premium">Premium</option>
                <option value="restricted">Restricted</option>
              </select>
            </div>

            {formData.material_type === 'lectures' && (
              <div className="form-group">
                <label>Duration (seconds)</label>
                <input
                  type="number"
                  name="duration"
                  value={formData.duration}
                  onChange={onInputChange}
                />
              </div>
            )}
          </div>

          {!isEdit && (
            <div className="form-group">
              <label>File *</label>
              <input
                type="file"
                required
                onChange={onFileSelect}
                accept={
                  formData.material_type === 'lectures' ? 'video/*' :
                  formData.material_type === 'books' ? '.pdf,.doc,.docx' :
                  'image/*,.pdf,.doc,.docx'
                }
              />
              {selectedFile && (
                <small className="file-info">
                  📎 {selectedFile.name} ({Math.round(selectedFile.size / 1024)}KB)
                </small>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Tags (JSON format)</label>
            <textarea
              name="tags"
              placeholder='{"category": "advanced", "difficulty": "hard"}'
              value={formData.tags}
              onChange={onInputChange}
              rows="2"
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={uploading}>
              {uploading ? 'Processing...' : (isEdit ? 'Update Material' : 'Create Material')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialsCRUD;
